import './FilterPanel.less';
import React from 'react';
import {translate} from "../../i18n";
import {setClickOutsideToClose} from "../../dom/event-handlers/click-outside-to-close";
import {jsonCompare} from "../../utils/json";
import Icon from "../icon/Icon";
import NumberFilterView from "./filter-views/number/NumberFilterView";
import StringFilterView from "./filter-views/string/StringFilterView";
import SelectableFilterView from "./filter-views/selectable/SelectableFilterView";
import NumberFilterEditor from "./filter-editors/number/NumberFilterEditor";
import StringFilterEditor from "./filter-editors/string/StringFilterEditor";
import SelectableFilterEditor from "./filter-editors/selectable/SelectableFilterEditor";
import Dropdown from "../dropdown/Dropdown";
import typedOperators from "./config/typedOperators";


export default class FilterPanel extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            filters: this.normalizeFilters(props.filters),
            editingField: '',
            filterMenuShown: false
        };

        this.filterListItems = [];
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.handleClickOutside();
        this.syncNewFilters(prevProps);
    }

    componentDidCatch(error, info) {
        // reset on error when filter config changed
        // but the user access page with old filter state
        this.setState({
            filters: [],
            editingField: '',
            filterMenuShown: false
        }, () => {
            this.pushFilters();
        });

        console.error(error);
    }

    syncNewFilters = (prevProps) => {
        if (jsonCompare(this.props.filters, prevProps.filters)) {
            return;
        }

        if (this.state.editingField === '') {
            this.setState({
                filters: this.normalizeFilters(this.props.filters)
            });
        }
    };

    normalizeFilters = (filters) => {
        const {filtersConfig} = this.props;

        return filters.filter((item) => {
            const config = filtersConfig[item.field];
            if (!config) {
                return false;
            }

            const operators = typedOperators[config.type];
            if (!operators || !operators.includes(item.operator)) {
                return false;
            }

            return true;
        });
    };

    getFilterableFields = () => {
        const {filtersConfig, filterableFields} = this.props;

        return (filterableFields || Object.keys(filtersConfig)).filter((field) => {
            const config = filtersConfig[field];
            if (!config) {
                return false;
            }

            const operators = typedOperators[config.type];
            if (!operators) {
                return false;
            }

            return true;
        });
    };

    handleClickOutside = () => {
        setClickOutsideToClose('components.FilterPanel.filterEditor', this.filterListItems, () => {
            if (!this.getIsEditingNewFilter()) {
                this.editFilter('');
            }
        });

        this.filterListItems = [];
    };

    isAllFiltersAdded = () => {
        return this.state.filters.length === this.getFilterableFields().length;
    };

    getFulfilledFilters() {
        return this.state.filters.filter(({operator}) => operator !== '');
    }

    pushFilters() {
        const fulfilledFilters = this.getFulfilledFilters();
        this.props.onChange(fulfilledFilters);
    }

    toggleFilterMenu = () => {
        this.setState({
            filters: this.getFulfilledFilters(),
            filterMenuShown: this.isAllFiltersAdded() ? false : !this.state.filterMenuShown
        });
    };

    openFilterMenu = () => {
        this.setState({filterMenuShown: true});
    };

    closeFilterMenu = () => {
        this.setState({filterMenuShown: false});
    };

    toggleFilter = (field) => {
        if (!this.removeFilter(field)) {
            this.addFilter(field);
            this.editFilter(field);
            this.setState({filterMenuShown: false});
        }
    };

    /**
     *
     * @param {string} field
     * @return {boolean}
     */
    removeFilter = (field) => {
        const {filters} = this.state;

        let index = -1;
        for (let i = 0; i < filters.length; i++) {
            if (filters[i].field === field) {
                index = i;
                break;
            }
        }

        if (index === -1) {
            return false;
        }

        const needUpdate = !!filters[index].operator;
        filters.splice(index, 1);
        this.setState({filters}, () => {
            if (needUpdate) {
                this.pushFilters();
            }
        });

        return true;
    };

    addFilter = (field) => {
        const {filters} = this.state;
        const config = this.props.filtersConfig[field];
        let value = '';
        if (config.type === 'selectable') {
            value = [];
        }
        const filter = {field: field, operator: '', value};

        filters.push(filter);
        this.setState({filters});
    };

    editFilter = (field) => {
        this.setState({editingField: field});
    };

    getIsEditingNewFilter = () => {
        const {filters, editingField} = this.state;
        const editingFilter = filters.find(({field}) => editingField === field);
        return editingFilter && editingFilter.operator === '';
    };

    clearFilters = () => {
        this.setState({filters: []}, () => {
            this.pushFilters();
        });
    };

    updateFilter = (field, operator, value) => {
        const {filters} = this.state;
        const filter = filters.find(item => item.field === field);
        if (filter) {
            filter.operator = operator;
            filter.value = value;
            this.setState({filters}, () => {
                this.pushFilters();
            });
        }
    };

    getFilterEditor(field) {
        const filter = this.state.filters.find(item => item.field === field);
        const config = this.props.filtersConfig[field];
        switch (config.type) {
            case 'number':
                return <NumberFilterEditor
                    fieldName={config.name}
                    defaultOperator={filter && filter.operator}
                    defaultValue={filter && filter.value}
                    optPrefix={config.options && config.options.prefix}
                    optSuffix={config.options && config.options.suffix}
                    optFormatDisabled={config.options && config.options.formatDisabled}
                    vMin={config.rules && config.rules.min}
                    vMax={config.rules && config.rules.max}
                    vDecimalDigits={config.rules && config.rules.decimalDigits}
                    vSelectableOptions={config.rules && config.rules.options}
                    update={(operator, value) => {
                        this.updateFilter(field, operator, value);
                        this.editFilter('');
                    }}
                />;
            case 'string':
                return <StringFilterEditor
                    fieldName={config.name}
                    defaultOperator={filter && filter.operator}
                    defaultValue={filter && filter.value}
                    vMinLength={config.rules && config.rules.minLength}
                    vMaxLength={config.rules && config.rules.maxLength}
                    update={(operator, value) => {
                        this.updateFilter(field, operator, value);
                        this.editFilter('');
                    }}
                />;
            case 'selectable':
                return <SelectableFilterEditor
                    fieldName={config.name}
                    defaultValue={filter && filter.value}
                    options={config.rules && config.rules.options}
                    optSingle={config.options && config.options.single}
                    update={(operator, value) => {
                        this.updateFilter(field, operator, value);
                        this.editFilter('');
                    }}
                />;
        }
    }

    isFieldEditable = (field) => {
        const {editingField} = this.state;

        const editing = field === editingField;

        return editing || !this.getIsEditingNewFilter();
    };

    getFilterView(field) {
        const {filtersConfig} = this.props;
        const {filters} = this.state;

        const config = filtersConfig[field];
        const filter = filters.find(item => item.field === field);

        if (!filter) {
            return null;
        }

        const editable = this.isFieldEditable(field);

        const requestEdit = editable ? () => {
            if (!this.getIsEditingNewFilter()) {
                this.editFilter(field);
            }
        } : undefined;

        switch (config.type) {
            case 'number':
                return <NumberFilterView
                    fieldName={config.name}
                    operator={filter.operator}
                    value={filter.value}
                    requestEdit={requestEdit}
                    optPrefix={config.options && config.options.prefix}
                    optSuffix={config.options && config.options.suffix}
                    optFormatDisabled={config.options && config.options.formatDisabled}
                    vDecimalDigits={config.rules && config.rules.decimalDigits}
                    vSelectableOptions={config.rules && config.rules.options}
                />;
            case 'string':
                return <StringFilterView
                    fieldName={config.name}
                    operator={filter.operator}
                    value={filter.value}
                    requestEdit={requestEdit}
                />;
            case 'selectable':
                return <SelectableFilterView
                    fieldName={config.name}
                    value={filter.value}
                    options={config.rules && config.rules.options}
                    optSingle={config.options && config.options.single}
                    requestEdit={requestEdit}
                />;
        }
    }

    render() {
        const {filtersConfig} = this.props;
        const {filters, filterMenuShown, editingField} = this.state;

        return (
            <div className="filter-panel">
                <div
                    className="filter-node"
                    onClick={this.openFilterMenu}
                >
                    <Icon name="filter"/>
                </div>

                <ul className="filter-list">
                    {
                        filters.map(({field, operator, value}) => {
                            const editing = field === editingField;
                            const editable = this.isFieldEditable(field);

                            return (
                                <li key={field}
                                    ref={el => el && this.filterListItems.push(el)}
                                >
                                    <div className="filter-wrapper">
                                        {
                                            editing
                                                ? this.getFilterEditor(field)
                                                : this.getFilterView(field)
                                        }
                                        <button
                                            type="button"
                                            className="remove-button"
                                            onClick={() => this.removeFilter(field)}
                                            disabled={!editable}
                                        >
                                            <Icon name="times"/>
                                        </button>
                                    </div>
                                </li>
                            );
                        })
                    }
                    {
                        filters.every(item => item.operator) &&
                        !this.isAllFiltersAdded() &&
                        <li>
                            <div
                                className={'add-button ' + (filterMenuShown ? 'active' : '')}
                                onClick={this.toggleFilterMenu}
                                ref={el => this.filterMenuButton = el}
                            >
                                <Icon name="plus"/>
                                <span>{translate('Add filter')}</span>
                            </div>
                            {
                                filterMenuShown &&
                                <Dropdown
                                    name="filter-panel"
                                    opener={this.filterMenuButton}
                                    minWidth="anchorWidth"
                                    close={this.closeFilterMenu}
                                >
                                    {
                                        contentViewableMaxHeight => <div className="menu-list">
                                            <div className="dropdown-scroll-container">
                                                <div
                                                    className="dropdown-scroll-view"
                                                    style={{
                                                        maxHeight: contentViewableMaxHeight + 'px'
                                                    }}
                                                >
                                                    {
                                                        this.getFilterableFields().map(field => {
                                                            const config = filtersConfig[field];
                                                            const selected = filters.find(filter => filter.field === field);
                                                            return (
                                                                <div key={field}
                                                                     className={'menu-item' + (selected ? ' selected' : '')}
                                                                     onClick={() => this.toggleFilter(field)}
                                                                >
                                                                    <span className="name">{config.name}</span>
                                                                    <Icon name="tick"/>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </Dropdown>
                            }
                        </li>
                    }
                </ul>
                {
                    this.getFulfilledFilters().length > 0 &&
                    <button
                        type="button"
                        className="clear-button"
                        onClick={this.clearFilters}
                    >
                        {translate('Clear all filters')}
                    </button>
                }
            </div>
        );
    }
}
